import React from 'react';
import { clsx } from 'clsx';

interface TaskProgressRingProps {
  completed: number;
  total: number;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showLabel?: boolean;
  showPercentage?: boolean;
  color?: 'primary' | 'success' | 'warning' | 'error';
  className?: string;
}

const sizeConfig = {
  sm: { ring: 40, stroke: 4, textSize: 'text-xs', labelSize: 'text-[10px]' },
  md: { ring: 60, stroke: 5, textSize: 'text-sm', labelSize: 'text-xs' },
  lg: { ring: 80, stroke: 6, textSize: 'text-lg', labelSize: 'text-sm' },
  xl: { ring: 120, stroke: 8, textSize: 'text-2xl', labelSize: 'text-base' },
};

const colorConfig = {
  primary: { stroke: '#3b82f6', bg: '#eff6ff' },
  success: { stroke: '#22c55e', bg: '#f0fdf4' },
  warning: { stroke: '#f59e0b', bg: '#fffbeb' },
  error: { stroke: '#ef4444', bg: '#fef2f2' },
};

const TaskProgressRing: React.FC<TaskProgressRingProps> = ({
  completed,
  total,
  size = 'md',
  showLabel = true,
  showPercentage = false,
  color = 'primary',
  className,
}) => {
  const config = sizeConfig[size];
  const colors = colorConfig[color];
  
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  const radius = (config.ring - config.stroke) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  // Determine color based on percentage if not specified
  const getAutoColor = () => {
    if (percentage >= 80) return colorConfig.success;
    if (percentage >= 50) return colorConfig.primary;
    if (percentage >= 25) return colorConfig.warning;
    return colorConfig.error;
  };

  const activeColor = color === 'primary' ? getAutoColor() : colors;

  return (
    <div className={clsx('relative inline-flex items-center justify-center', className)}>
      <svg
        width={config.ring}
        height={config.ring}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={config.ring / 2}
          cy={config.ring / 2}
          r={radius}
          strokeWidth={config.stroke}
          stroke="#e5e7eb"
          fill="transparent"
        />
        {/* Progress circle */}
        <circle
          cx={config.ring / 2}
          cy={config.ring / 2}
          r={radius}
          strokeWidth={config.stroke}
          stroke={activeColor.stroke}
          fill="transparent"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-500 ease-out"
        />
      </svg>

      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {showPercentage ? (
          <span className={clsx('font-bold', config.textSize)} style={{ color: activeColor.stroke }}>
            {percentage}%
          </span>
        ) : (
          <>
            <span className={clsx('font-bold', config.textSize)} style={{ color: activeColor.stroke }}>
              {completed}
            </span>
            {showLabel && (
              <span className={clsx('text-gray-400', config.labelSize)}>
                of {total}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TaskProgressRing;

